import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import api, { telegramApi } from '../services/api';
import universalNotificationService from '../services/universalNotificationService';

const SettingsPage: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Профиль
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  
  // Пароль
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Настройки приложения (кешируются в localStorage, переживают перезагрузку)
  const { notifications, autoUpdate, setNotifications, setAutoUpdate } = useSettingsStore();

  // ── Telegram bot config (superadmin only) ──────────────────────────────────
  const [tgConfig, setTgConfig] = useState<any>(null);
  const [tgLoading, setTgLoading] = useState(false);
  const [tgSaving, setTgSaving] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const loadTgConfig = async () => {
    setTgLoading(true);
    try {
      const res = await telegramApi.getConfig();
      const data = res.data?.data || {};
      setTgConfig(data);
      setWebhookUrl(data.webhookUrl || '');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить настройки бота');
    } finally {
      setTgLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'telegram' && user?.role === 'superadmin' && !tgConfig) {
      loadTgConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTgSave = async () => {
    const body: { botToken?: string; webhookUrl?: string; webhookSecret?: string } = {};
    if (newToken.trim()) body.botToken = newToken.trim();
    if (webhookUrl.trim() !== (tgConfig?.webhookUrl || '')) body.webhookUrl = webhookUrl.trim();
    if (webhookSecret.trim()) body.webhookSecret = webhookSecret.trim();
    if (Object.keys(body).length === 0) {
      toast('Нет изменений для сохранения', { icon: 'ℹ️' });
      return;
    }
    setTgSaving(true);
    try {
      const res = await telegramApi.updateConfig(body);
      toast.success(res.data?.message || 'Настройки сохранены');
      setNewToken('');
      setWebhookSecret('');
      await loadTgConfig();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка сохранения настроек бота');
    } finally {
      setTgSaving(false);
    }
  };

  const handleRebindWebhook = async () => {
    setTgSaving(true);
    try {
      await telegramApi.setWebhook();
      toast.success('Webhook перепривязан');
      await loadTgConfig();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка привязки webhook');
    } finally {
      setTgSaving(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile({ firstName, lastName, avatar });
      toast.success('Профиль обновлен');
    } catch (error) {
      toast.error('Ошибка обновления профиля');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Пароль должен содержать минимум 6 символов');
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Пароль изменен');
    } catch (error) {
      toast.error('Ошибка изменения пароля');
    }
  };

  const isSuperadmin = user?.role === 'superadmin';

  const tabs = [
    { id: 'profile', name: 'Профиль' },
    { id: 'password', name: 'Пароль' },
    { id: 'preferences', name: 'Настройки' },
    ...(isSuperadmin ? [{ id: 'telegram', name: 'Telegram-бот' }] : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Настройки</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Управление профилем и настройками аккаунта
        </p>
      </div>

      <div className="bg-card border border-border shadow rounded-lg">
        {/* Табы */}
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Профиль */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
                    Имя
                  </label>
                  <Input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
                    Фамилия
                  </label>
                  <Input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="avatar" className="block text-sm font-medium text-foreground">
                  URL аватара
                </label>
                <Input
                  type="url"
                  id="avatar"
                  value={avatar}
                  onChange={(e) => setAvatar(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit">
                  Сохранить
                </Button>
              </div>
            </form>
          )}

          {/* Пароль */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-foreground">
                  Текущий пароль
                </label>
                <Input
                  type="password"
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-foreground">
                  Новый пароль
                </label>
                <Input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">
                  Подтвердите пароль
                </label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit">
                  Изменить пароль
                </Button>
              </div>
            </form>
          )}

          {/* Настройки */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Уведомления</h3>
                  <p className="text-sm text-muted-foreground">Получать уведомления о новых заявках</p>
                </div>
                <Switch
                  checked={notifications}
                  onCheckedChange={setNotifications}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Push на это устройство</h3>
                  <p className="text-sm text-muted-foreground">Приходят, даже когда вкладка закрыта. Проверьте, что доходит.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const ok = await universalNotificationService.subscribe();
                      if (!ok) {
                        toast.error(typeof Notification !== 'undefined' && Notification.permission === 'denied'
                          ? 'Уведомления запрещены в браузере: замок слева от адреса → Уведомления → Разрешить'
                          : 'Не удалось включить уведомления на этом устройстве');
                        return;
                      }
                      const res = await api.post('/api/fcm-tokens/test');
                      toast.success(res.data?.message || 'Тестовое уведомление отправлено');
                    } catch (e: any) {
                      toast.error(e?.response?.data?.message || 'Не удалось отправить тестовое уведомление');
                    }
                  }}
                >
                  Проверить уведомление
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Автообновление</h3>
                  <p className="text-sm text-muted-foreground">Автоматически обновлять данные</p>
                </div>
                <Switch
                  checked={autoUpdate}
                  onCheckedChange={setAutoUpdate}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-foreground">Темная тема</h3>
                  <p className="text-sm text-muted-foreground">Использовать темную тему интерфейса</p>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={() => toggleTheme()}
                />
              </div>
            </div>
          )}

          {/* Telegram-бот (только суперадмин) */}
          {activeTab === 'telegram' && isSuperadmin && (
            <div className="space-y-6">
              {tgLoading && !tgConfig ? (
                <p className="text-sm text-muted-foreground">Загрузка…</p>
              ) : (
                <>
                  {/* Текущее состояние */}
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Бот</span>
                      <span className="text-sm font-medium text-foreground">
                        {tgConfig?.botUsername ? `@${tgConfig.botUsername}` : '— не настроен —'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Токен</span>
                      <span className="text-sm font-mono text-foreground">
                        {tgConfig?.hasToken ? tgConfig.tokenMask : 'не задан'}
                        {tgConfig?.tokenSource === 'env' && (
                          <span className="ml-2 text-[10px] text-amber-500">(из .env)</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Webhook</span>
                      <span className="text-sm text-foreground truncate max-w-[60%]">
                        {tgConfig?.webhookInfo?.url || '— не привязан —'}
                      </span>
                    </div>
                    {tgConfig?.webhookInfo?.lastErrorMessage && (
                      <div className="text-xs text-rose-500">
                        Ошибка webhook: {tgConfig.webhookInfo.lastErrorMessage}
                      </div>
                    )}
                    {typeof tgConfig?.webhookInfo?.pendingUpdateCount === 'number'
                      && tgConfig.webhookInfo.pendingUpdateCount > 0 && (
                      <div className="text-xs text-amber-500">
                        Необработанных апдейтов: {tgConfig.webhookInfo.pendingUpdateCount}
                      </div>
                    )}
                  </div>

                  {/* Смена токена */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Новый токен бота
                      </label>
                      <p className="text-xs text-muted-foreground mb-1">
                        Получите у @BotFather. Будет проверен через getMe перед сохранением.
                        Оставьте пустым, чтобы не менять.
                      </p>
                      <Input
                        type="password"
                        value={newToken}
                        onChange={(e) => setNewToken(e.target.value)}
                        placeholder="123456789:AA..."
                        className="mt-1 font-mono"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        URL webhook
                      </label>
                      <Input
                        type="url"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://ggdesk.ru/api/telegram/webhook"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground">
                        Секрет webhook {tgConfig?.webhookSecretSet && (
                          <span className="text-xs text-success-500">(установлен)</span>
                        )}
                      </label>
                      <p className="text-xs text-muted-foreground mb-1">
                        Проверяется в заголовке от Telegram. Оставьте пустым, чтобы не менять.
                      </p>
                      <Input
                        type="password"
                        value={webhookSecret}
                        onChange={(e) => setWebhookSecret(e.target.value)}
                        placeholder="••••••••"
                        className="mt-1 font-mono"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 justify-between">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRebindWebhook}
                      disabled={tgSaving}
                    >
                      Перепривязать webhook
                    </Button>
                    <Button type="button" onClick={handleTgSave} disabled={tgSaving}>
                      {tgSaving ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                  </div>

                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
                    ⚠️ При смене токена бот меняется на нового. Webhook
                    перепривязывается автоматически. Операторы продолжат работать,
                    но клиентам нужно будет писать уже новому боту.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;